import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { promotedListingIds } from '../../config/configListing';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchPageAssets } from '../../ducks/hostedAssets.duck';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';

export const ASSET_NAME = 'landing-page';

const LISTINGS_PER_SECTION = 8;
const LISTING_IMAGE_VARIANT = 'listing-card';
const LISTING_FIELDS = [
  'title',
  'geolocation',
  'price',
  'deleted',
  'state',
  'publicData.listingType',
  'publicData.transactionProcessAlias',
  'publicData.unitType',
  'publicData.unit_type',
  'publicData.cardStyle',
  'publicData.pickupEnabled',
  'publicData.shippingEnabled',
];

const buildQueryParams = variantPrefix => ({
  perPage: LISTINGS_PER_SECTION,
  page: 1,
  minStock: 1,
  stockMode: 'match-undefined',
  include: ['images', 'author'],
  'fields.listing': LISTING_FIELDS,
  'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
  'fields.image': [
    `variants.${variantPrefix}`,
    `variants.${variantPrefix}-2x`,
    'variants.scaled-small',
    'variants.scaled-medium',
  ],
  'limit.images': 1,
  ...createImageVariantConfig(`${variantPrefix}`, 400, 0.75),
  ...createImageVariantConfig(`${variantPrefix}-2x`, 800, 0.75),
});

// ================ Async Thunks ================ //

export const fetchFeaturedProducts = createAsyncThunk(
  'LandingPage/fetchFeaturedProducts',
  async (_arg, { dispatch, rejectWithValue, extra: sdk }) => {
    return sdk.listings
      .query({
        'pub_listingType': 'products',
        ...buildQueryParams(LISTING_IMAGE_VARIANT),
      })
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        return response.data.data.map(l => l.id);
      })
      .catch(e => {
        log.error(e, 'landing-page-fetch-products-failed');
        return rejectWithValue(storableError(e));
      });
  }
);

export const fetchFeaturedServices = createAsyncThunk(
  'LandingPage/fetchFeaturedServices',
  async (_arg, { dispatch, rejectWithValue, extra: sdk }) => {
    return sdk.listings
      .query({
        'pub_listingType': 'services',
        ...buildQueryParams(LISTING_IMAGE_VARIANT),
      })
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        return response.data.data.map(l => l.id);
      })
      .catch(e => {
        log.error(e, 'landing-page-fetch-services-failed');
        return rejectWithValue(storableError(e));
      });
  }
);

export const fetchPromotedListings = createAsyncThunk(
  'LandingPage/fetchPromotedListings',
  async (_arg, { dispatch, getState, rejectWithValue, extra: sdk }) => {
    const state = getState();
    
    // 1. Try to read from landing-page hosted asset's "promotion" section description
    const landingPageAsset = state.hostedAssets?.pageAssetsData?.landingPage?.data;
    const sections = landingPageAsset?.sections || [];
    const promotionSection = sections.find(
      s => s.sectionId === 'promotion' || s.sectionName === 'promotion' || s.sectionType === 'promotion'
    );
    
    let descriptionText = '';
    if (promotionSection?.description) {
      if (typeof promotionSection.description === 'string') {
        descriptionText = promotionSection.description;
      } else if (promotionSection.description.content) {
        descriptionText = promotionSection.description.content;
      }
    }
    
    let hostedIds = descriptionText
      ? descriptionText
          .split(/[\s,]+/)
          .map(id => id.trim())
          .filter(id => id.length > 0)
      : [];
      
    // 2. Fallback to hosted assets if any
    if (hostedIds.length === 0) {
      hostedIds = state.hostedAssets?.appAssets?.promotedListings?.data?.listingIds || [];
    }
    
    // Fallback to local config promotedListingIds from configListing.js
    let listingIds = [...hostedIds];
    if (listingIds.length === 0) {
      listingIds = [...promotedListingIds];
    }

    const queryParams = buildQueryParams(LISTING_IMAGE_VARIANT);

    if (listingIds.length > 0) {
      // Query by IDs (limit to 100 as per Sharetribe limit)
      return sdk.listings
        .query({
          ids: listingIds.slice(0, 100),
          ...queryParams,
        })
        .then(response => {
          dispatch(addMarketplaceEntities(response));
          return response.data.data.map(l => l.id);
        })
        .catch(e => {
          log.error(e, 'landing-page-fetch-promoted-by-ids-failed');
          return rejectWithValue(storableError(e));
        });
    } else {
      return [];
    }
  }
);

// ================ Slice ================ //

const landingPageSlice = createSlice({
  name: 'LandingPage',
  initialState: {
    products: { listingIds: [], inProgress: false, error: null },
    services: { listingIds: [], inProgress: false, error: null },
    promoted: { listingIds: [], inProgress: false, error: null },
  },
  reducers: {},
  extraReducers: builder => {
    builder
      // Products
      .addCase(fetchFeaturedProducts.pending, state => {
        state.products.inProgress = true;
        state.products.error = null;
      })
      .addCase(fetchFeaturedProducts.fulfilled, (state, action) => {
        state.products.inProgress = false;
        state.products.listingIds = action.payload;
      })
      .addCase(fetchFeaturedProducts.rejected, (state, action) => {
        state.products.inProgress = false;
        state.products.error = action.payload;
      })
      // Services
      .addCase(fetchFeaturedServices.pending, state => {
        state.services.inProgress = true;
        state.services.error = null;
      })
      .addCase(fetchFeaturedServices.fulfilled, (state, action) => {
        state.services.inProgress = false;
        state.services.listingIds = action.payload;
      })
      .addCase(fetchFeaturedServices.rejected, (state, action) => {
        state.services.inProgress = false;
        state.services.error = action.payload;
      })
      // Promoted
      .addCase(fetchPromotedListings.pending, state => {
        state.promoted.inProgress = true;
        state.promoted.error = null;
      })
      .addCase(fetchPromotedListings.fulfilled, (state, action) => {
        state.promoted.inProgress = false;
        state.promoted.listingIds = action.payload;
      })
      .addCase(fetchPromotedListings.rejected, (state, action) => {
        state.promoted.inProgress = false;
        state.promoted.error = action.payload;
      });
  },
});

export const loadData = () => (dispatch, _getState) => {
  const pageAsset = { landingPage: 'content/pages/landing-page.json' };
  return Promise.all([
    dispatch(fetchPageAssets(pageAsset, true)),
    dispatch(fetchFeaturedProducts()),
    dispatch(fetchFeaturedServices()),
  ]).then(() => {
    return dispatch(fetchPromotedListings());
  });
};

export default landingPageSlice.reducer;
